/* eslint-disable max-len */

/**
 * react-dropzone's native implementation of fileRejections came with more information than we needed
 * and created needless complexity, being an array of objects of arrays of objects as shown below
 * [{file: File, errors: [{code: '', message: ''}]}]
 * This function flattens the native fileRejections to a simple array of objects
 * with string properties: { fileName: '', error: '' }
 *
 * @param {Array[{file: File, errors: [{code: '', message: ''}]}]} fileRejections - An array of file rejection objects.
 * Each object should have a 'file' property with a 'name' and an 'errors' array.
 * Each error object in 'errors' should have a 'code' property.
 *
 * @returns {Array[{ fileName: '', error: '' }]} A flattened array of objects with 'fileName' and 'error' properties.
 *
 * @throws {Error} Throws an error if the input is not an array,
 * or if a fileRejection object is malformed.
 */
export const flattenFileRejections = (fileRejections) => {
  if (!Array.isArray(fileRejections)) {
    throw new Error('Input must be an array');
  }

  return fileRejections.reduce((acc, { file, errors }) => {
    if (!file || !Array.isArray(errors)) {
      throw new Error('Malformed fileRejection object');
    }
    errors.forEach(({ code }) => {
      acc.push({ fileName: file.name, error: code });
    });
    return acc;
  }, []);
};

/**
 * Determines the file type of a given file based on its name and extension.
 *
 * @param {string} fileName - The name of the file.
 *
 * @returns {string} The file type as a string ('image', 'video', 'pdf', 'embedded_video', or 'unsupported').
 *
 * @throws {Error} Throws an error if fileName is not a string or is empty.
 */
export const getFileType = (fileName) => {
  if (typeof fileName !== 'string' || fileName.trim() === '') {
    throw new Error('Invalid fileName. Must be a non-empty string');
  }
  const imageTypes = /\.(jpe?g|png)$/i;
  const videoTypes = /\.(mov|mp4)$/i;
  const pdfTypes = /\.pdf$/i;
  let fileType = '';
  if (imageTypes.test(fileName)) {
    fileType = 'image';
  } else if (videoTypes.test(fileName)) {
    fileType = 'video';
  } else if (pdfTypes.test(fileName)) {
    fileType = 'pdf';
  } else {
    fileType = 'unsupported';
  }
  return fileType;
};

/**
 * Validates file names to ensure uniqueness across acceptedFiles and currentAddOns.
 *
 * @param {Array} acceptedFiles - An array of accepted files, each having a 'name' property.
 * @param {Array} fileRejections - An array of file rejection objects with 'fileName' and 'error' properties.
 * @param {Array} currentAddOns - An array of current add-ons, each having a 'fileName' property.
 *
 * @returns {Array} An array containing two elements:
 *                  1. An array of unique files from acceptedFiles.
 *                  2. An updated array of fileRejections.
 *
 * @throws {Error} Throws an error if any of the inputs are not arrays.
 */
export const uniqueNameValidator = (acceptedFiles, fileRejections, currentAddOns) => {
  if (!Array.isArray(acceptedFiles) || !Array.isArray(fileRejections) || !Array.isArray(currentAddOns)) {
    throw new Error('All inputs must be arrays');
  }

  const uniqueFiles = acceptedFiles.filter((file, index, self) => {
    const isDuplicateInAcceptedFiles = index !== self.findIndex((f) => file.name === f.name);
    const isDuplicateInCurrentAddOns = currentAddOns.findIndex((f) => file.name === f.fileName) !== -1;

    if (isDuplicateInAcceptedFiles || isDuplicateInCurrentAddOns) {
      fileRejections.push({ fileName: file.name, error: 'duplicate-file' });
    }

    return !isDuplicateInAcceptedFiles && !isDuplicateInCurrentAddOns;
  });

  return [uniqueFiles, fileRejections];
};
